import type { ReactNode } from 'react'
import { useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import { Menu, toastManager } from '@systemeio/components'
import {
  DotsVerticalIcon,
  Pencil01Icon,
  Pin02Icon,
  ReportMessageIcon,
  Trash02Icon,
  XCloseIcon,
} from '@systemeio/icons'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next/pages'
import Confirmation from 'shared/components/confirmation-modal'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { useDeleteComment } from 'modules/comments/api/delete-comment'
import { usePinComment } from 'modules/comments/api/pin-comment'
import { useUnpinComment } from 'modules/comments/api/unpin-comment'
import type { CommentInterface } from 'modules/comments/components/comment/comment.types'
import { useCommentsContext } from 'modules/comments/components/comments-list'
import { isCommunityExtra } from 'modules/comments/utils/comments-extra-type-guards'
import { communityKeys, communityReportKeys } from 'modules/community/api/key-factory'
import { banUser } from 'modules/community/api/posts-api'
import { useReportComment } from 'modules/community/api/report-comment'
import { ReportContentModal } from 'modules/community/components/report-content/report-content-modal'
import { ACTIVITY_CLEANUP_PERIOD_DURATION } from 'modules/community/const/post-consts'
import { BanPeriodUnitEnum } from 'modules/community/enums/member-enum'
import useIsUserMember from 'modules/community/hooks/use-is-user-member'
import { RemoveRecentActivitiesCheckbox } from 'modules/community/post/remove-recent-activities-checkbox'

interface CommentActionsProps {
  onEdit: () => void
  className?: string
  commentData: CommentInterface
}

export default function CommentActions({
  onEdit,
  className,
  commentData,
}: CommentActionsProps): ReactNode {
  const { t } = useTranslation()
  const { user } = useUser()
  const queryClient = useQueryClient()
  const { extra, pinnedComments, resourceId, commentsType } = useCommentsContext()
  const reportCommentMutation = useReportComment({})

  const [removeRecentActivitiesChecked, setRemoveRecentActivitiesChecked] = useState(false)
  const [isDeleteAndBanUserOpened, setIsDeleteAndBanUserOpened] = useState(false)
  const [isDeleteCommentOpened, setIsDeleteCommentOpened] = useState(false)
  const [switchPinnedCommentModalOpened, setSwitchPinnedCommentModalOpened] = useState(false)
  const [isReportOpened, setIsReportOpened] = useState(false)

  const pinCommentMutation = usePinComment({
    commentId: commentData.id,
    pageId: resourceId,
    onSuccess: () => {
      setSwitchPinnedCommentModalOpened(false)
      toastManager.add({ type: 'success', title: t('comments.actions.pin_comment.success') })
    },
    onError: () => {
      toastManager.add({ type: 'error', title: t('core.error.internal_error_message') })
    },
  })
  const unpinCommentMutation = useUnpinComment({
    commentId: commentData.id,
    pageId: resourceId,
    onSuccess: () => {
      toastManager.add({ type: 'success', title: t('comments.actions.unpin_comment.success') })
    },
    onError: () => {
      toastManager.add({ type: 'error', title: t('core.error.internal_error_message') })
    },
  })

  const { canDeleteComment, canDeleteCommentAndBanAuthor, canUpdateComment, canPinComment } =
    commentData
  const isCommunityComments = isCommunityExtra(extra)
  const communityPath = isCommunityComments ? extra.community.communityPath : ''
  const postId = isCommunityComments ? extra.community.postId : 0
  const { isUserMember } = useIsUserMember(communityPath || undefined)

  const isOwnComment = Boolean(user && isAuthorizedUser(user) && user.id === commentData.userId)

  const canReportComment =
    Boolean(isUserMember) && Boolean(communityPath) && postId > 0 && !isOwnComment

  const isCanManagePinsAndNotReplyComment = canPinComment && Array.isArray(commentData.parent)

  const deleteCommentMutation = useDeleteComment({
    commentsType,
    pageId: resourceId,
    onSuccess: () => {
      setIsDeleteCommentOpened(false)
      if (!isCommunityComments) return
      void queryClient.invalidateQueries({
        queryKey: communityKeys.postsCommentsCount(extra.community.communityPath, [
          extra.community.postId,
        ]),
      })
      void queryClient.invalidateQueries({ queryKey: communityKeys.postsCommentsGeneral() })
      void queryClient.invalidateQueries({ queryKey: communityReportKeys.all })
    },
    onError: error => {
      toastManager.add({ type: 'error', title: t('comments.actions.delete_comment.failed') })
      Sentry.captureException(error)
    },
  })

  const handleDeleteAndBanUser = async (): Promise<void> => {
    if (!isCommunityComments) return
    await deleteCommentMutation.mutateAsync(commentData.id)

    try {
      await banUser(
        commentData.userId,
        extra.community.communityPath,
        removeRecentActivitiesChecked
          ? {
              deleteRecentActivityOptions: {
                periodUnit: BanPeriodUnitEnum.day,
                periodDuration: ACTIVITY_CLEANUP_PERIOD_DURATION,
              },
            }
          : undefined,
      )
      toastManager.add({
        type: 'success',
        title: t('comments.actions.delete_comment_and_ban_user.success'),
      })
    } catch (error) {
      toastManager.add({ type: 'error', title: t('members.ban_failed_toast') })
      throw error
    }
  }

  const menuItems = [
    ...(canDeleteComment
      ? [
          {
            id: 'comment-action-remove',
            label: (
              <span
                data-testid="comment-action-remove"
                className="flex w-full items-center gap-1.5"
              >
                <XCloseIcon className="size-5" />
                {t('comments.actions.remove')}
              </span>
            ),
            onSelect: () => {
              setIsDeleteCommentOpened(true)
            },
          },
        ]
      : []),
    ...(canUpdateComment
      ? [
          {
            id: 'comment-action-edit',
            label: (
              <span data-testid="comment-action-edit" className="flex w-full items-center gap-1.5">
                <Pencil01Icon className="size-5" />
                {t('comments.actions.edit')}
              </span>
            ),
            onSelect: onEdit,
          },
        ]
      : []),
    ...(canDeleteCommentAndBanAuthor && isCommunityComments
      ? [
          {
            id: 'comment-action-remove-and-ban',
            label: (
              <span
                data-testid="comment-action-remove-and-ban"
                className="flex w-full items-center gap-1.5"
              >
                <Trash02Icon className="size-5" />
                {t('comments.actions.remove_and_ban_user')}
              </span>
            ),
            onSelect: () => {
              setIsDeleteAndBanUserOpened(true)
            },
          },
        ]
      : []),
    ...(isCanManagePinsAndNotReplyComment
      ? [
          commentData?.isPinned
            ? {
                id: 'comment-action-unpin',
                label: (
                  <span className="flex w-full items-center gap-1.5">
                    <Pin02Icon className="size-5" />
                    {t('comments.actions.unpin')}
                  </span>
                ),
                onSelect: () => {
                  unpinCommentMutation.mutate()
                },
              }
            : {
                id: 'comment-action-pin',
                label: (
                  <span className="flex w-full items-center gap-1.5">
                    <Pin02Icon className="size-5" />
                    {t('comments.actions.pin')}
                  </span>
                ),
                onSelect: () => {
                  if (pinnedComments.length > 0) {
                    setSwitchPinnedCommentModalOpened(true)
                  } else {
                    pinCommentMutation.mutate()
                  }
                },
              },
        ]
      : []),
    ...(canReportComment
      ? [
          {
            id: 'comment-action-report',
            label: (
              <span className="flex w-full items-center gap-1.5">
                <ReportMessageIcon className="size-5" />
                {t('comments.actions.report')}
              </span>
            ),
            onSelect: () => {
              setIsReportOpened(true)
            },
          },
        ]
      : []),
  ]

  if (
    !(
      canDeleteComment ||
      canUpdateComment ||
      canDeleteCommentAndBanAuthor ||
      isCanManagePinsAndNotReplyComment ||
      canReportComment
    )
  ) {
    return null
  }

  return (
    <>
      <div
        className="contents"
        onClick={e => {
          e.stopPropagation()
        }}
      >
        <Menu
          trigger={
            <button type="button" data-testid="comment-actions-trigger">
              <div className="relative">
                <DotsVerticalIcon className="size-5" />
              </div>
            </button>
          }
          triggerProps={{ className }}
          items={menuItems}
          className="w-max min-w-[144px]"
        />
      </div>

      <ReportContentModal
        opened={isReportOpened}
        onClose={() => {
          setIsReportOpened(false)
        }}
        targetType="comment"
        isSubmitting={reportCommentMutation.isPending}
        onSubmit={async body => {
          await reportCommentMutation.mutateAsync({
            body,
            communityPath,
            postId,
            commentId: commentData.id,
          })
        }}
      />

      {isDeleteAndBanUserOpened && (
        <Confirmation
          onConfirm={handleDeleteAndBanUser}
          onCloseModal={() => {
            setIsDeleteAndBanUserOpened(false)
          }}
          confirmationContent={
            <RemoveRecentActivitiesCheckbox
              label={t('settings.form.delete_comment_and_ban_user_confirmation.label')}
              memberDisplayName={commentData.userName}
              checked={removeRecentActivitiesChecked}
              onCheck={() => {
                setRemoveRecentActivitiesChecked(prev => !prev)
              }}
            />
          }
          errorMessageMap={{
            unprocessableContentError: 'community.error.error_user_already_banned',
          }}
        />
      )}
      {isDeleteCommentOpened && (
        <Confirmation
          onConfirm={async () =>
            deleteCommentMutation.mutateAsync(commentData.id, {
              onSuccess: () => {
                toastManager.add({
                  type: 'success',
                  title: t('comments.actions.delete_comment.success'),
                })
              },
            })
          }
          onCloseModal={() => {
            setIsDeleteCommentOpened(false)
          }}
          confirmationContent={t('comments.components.comments.delete_confirmation_label')}
        />
      )}
      {switchPinnedCommentModalOpened && (
        <Confirmation
          onConfirm={async () => {
            await pinCommentMutation.mutateAsync()
          }}
          onCloseModal={() => {
            setSwitchPinnedCommentModalOpened(false)
          }}
          confirmationContent={t('comments.actions.switch_pin_comment.confirmation.content')}
        />
      )}
    </>
  )
}
